// NOTE: I added this exception because of the handleSubmit function and I can not use a one line exception
// because it does not seem to work with jsx
/* eslint-disable @typescript-eslint/no-misused-promises */
import { QuestionIcon } from '@chakra-ui/icons';
import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Switch,
  Textarea,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { AccessLinkTypes } from '@/@generated/codegen/API';

export const createAccessLinkPanelSchema = z
  .object({
    numberOfLinks: z.number().min(1, 'Must create at least 1 link'),
    validityDays: z.number().min(1, 'Validity must be at least 1 day'),
    activateMetadataDemo: z.boolean().default(false),
    linkFirstAccount: z.boolean(),
    shouldForceUsernames: z.boolean().default(false),
    forcedUsernames: z.preprocess((val) => {
      if (typeof val === 'string') {
        return val
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean);
      }
      return val; // already an array
    }, z.array(z.string()).default([])),
  })
  .superRefine((data, ctx) => {
    if (data.shouldForceUsernames) {
      if (data.forcedUsernames.length !== data.numberOfLinks) {
        ctx.addIssue({
          code: 'custom',
          message: 'Number of forced usernames must equal the number of links',
          path: ['forcedUsernames'],
        });
      }
    }
  });

export type CreateAccessLinkFormValues = z.infer<typeof createAccessLinkPanelSchema>;

type Props = {
  type: AccessLinkTypes;
  onClose: () => void;
  onSubmit: (values: CreateAccessLinkFormValues) => void;
  isCreating: boolean;
};

const getDefaults = (type: AccessLinkTypes): Partial<CreateAccessLinkFormValues> => {
  switch (type) {
    case AccessLinkTypes.CasePreview:
      return {
        numberOfLinks: 1,
        validityDays: 30,
        linkFirstAccount: true,
        shouldForceUsernames: true,
        forcedUsernames: ['preview-user'],
      };
    case AccessLinkTypes.MasterclassReadAccess:
      return {
        numberOfLinks: 1,
        validityDays: 30,
        linkFirstAccount: false,
        shouldForceUsernames: false,
      };
    case AccessLinkTypes.MeetingReadAccess:
      return {
        numberOfLinks: 1,
        validityDays: 30,
        linkFirstAccount: false,
        shouldForceUsernames: false,
      };
    case AccessLinkTypes.ClientRegistration:
      return {
        numberOfLinks: 1,
        validityDays: 30,
        linkFirstAccount: false,
        shouldForceUsernames: false,
      };
    default: {
      const _exhaustiveCheck: never = type;
      console.error('Unhandled type in getDefaults:', type);
      throw new Error(`Unhandled type in getDefaults`);
    }
  }
};

export function CreateAccessLinkPanelView({ type, onClose, onSubmit, isCreating }: Props) {
  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CreateAccessLinkFormValues>({
    resolver: zodResolver(createAccessLinkPanelSchema),
    defaultValues: getDefaults(type),
  });

  const linkFirstAccount = watch('linkFirstAccount');
  const shouldForceUsernames = watch('shouldForceUsernames');

  const shouldShowAmountOfLinks = type === AccessLinkTypes.MasterclassReadAccess;
  const shouldShowIsDemo = type === AccessLinkTypes.MasterclassReadAccess;

  // Enforce rules:
  useEffect(() => {
    if (!linkFirstAccount && shouldForceUsernames) {
      // Force-disable shouldForceUsernames if linkFirstAccount is false
      setValue('shouldForceUsernames', false, { shouldValidate: true });
    }
  }, [linkFirstAccount, shouldForceUsernames, setValue]);

  return (
    <Modal isOpen={true} onClose={onClose} size="3xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('admin.createAccessLinks.modal.title')}</ModalHeader>
        <ModalCloseButton />

        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody>
            <VStack align="stretch" spacing={6}>
              {/* How many links */}
              {shouldShowAmountOfLinks && (
                <FormControl isInvalid={!!errors.numberOfLinks}>
                  <Flex align="center" gap={2}>
                    <FormLabel m={0}>
                      {t('admin.createAccessLinks.form.numberOfLinks.label')}
                    </FormLabel>
                    <Tooltip label={t('admin.createAccessLinks.form.numberOfLinks.tooltip')}>
                      <IconButton
                        size="xs"
                        aria-label="Info"
                        icon={<QuestionIcon />}
                        variant="ghost"
                      />
                    </Tooltip>
                  </Flex>
                  <Controller
                    name="numberOfLinks"
                    control={control}
                    render={({ field }) => (
                      <NumberInput
                        min={1}
                        value={field.value}
                        onChange={(val) => field.onChange(Number(val))}
                        w="100%"
                      >
                        <NumberInputField />
                      </NumberInput>
                    )}
                  />
                  {errors.numberOfLinks && (
                    <FormErrorMessage>{errors.numberOfLinks.message}</FormErrorMessage>
                  )}
                </FormControl>
              )}

              {/* Validity in days */}
              <FormControl isInvalid={!!errors.validityDays}>
                <Flex align="center" gap={2}>
                  <FormLabel m={0}>
                    {t('admin.createAccessLinks.form.validityDays.label')}
                  </FormLabel>
                  <Tooltip label={t('admin.createAccessLinks.form.validityDays.tooltip')}>
                    <IconButton
                      size="xs"
                      aria-label="Info"
                      icon={<QuestionIcon />}
                      variant="ghost"
                    />
                  </Tooltip>
                </Flex>
                <HStack>
                  <Controller
                    name="validityDays"
                    control={control}
                    render={({ field }) => (
                      <NumberInput
                        min={1}
                        value={field.value}
                        onChange={(val) => field.onChange(Number(val))}
                      >
                        <NumberInputField />
                      </NumberInput>
                    )}
                  />
                  <HStack>
                    <Button size="sm" variant="outline" onClick={() => setValue('validityDays', 7)}>
                      1w
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setValue('validityDays', 30)}
                    >
                      1m
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setValue('validityDays', 90)}
                    >
                      3m
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setValue('validityDays', 365)}
                    >
                      1y
                    </Button>
                  </HStack>
                </HStack>
                {errors.validityDays && (
                  <FormErrorMessage>{errors.validityDays.message}</FormErrorMessage>
                )}
              </FormControl>

              {shouldShowIsDemo && (
                <FormControl>
                  <Flex align="center" gap={2}>
                    <FormLabel m={0}>{t('admin.createAccessLinks.form.isDemo.label')}</FormLabel>
                    <Tooltip label={t('admin.createAccessLinks.form.isDemo.tooltip')}>
                      <IconButton
                        size="xs"
                        aria-label="Info"
                        icon={<QuestionIcon />}
                        variant="ghost"
                      />
                    </Tooltip>
                  </Flex>
                  <Controller
                    name="activateMetadataDemo"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        isChecked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    )}
                  />
                </FormControl>
              )}

              {/* Link first account */}
              <FormControl>
                <Flex align="center" gap={2}>
                  <FormLabel m={0}>
                    {t('admin.createAccessLinks.form.linkFirstAccount.label')}
                  </FormLabel>
                  <Tooltip label={t('admin.createAccessLinks.form.linkFirstAccount.tooltip')}>
                    <IconButton
                      size="xs"
                      aria-label="Info"
                      icon={<QuestionIcon />}
                      variant="ghost"
                    />
                  </Tooltip>
                </Flex>
                <Controller
                  name="linkFirstAccount"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      isChecked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  )}
                />
              </FormControl>

              {/* Forced usernames toggle */}
              <FormControl>
                <Flex align="center" gap={2}>
                  <FormLabel m={0}>
                    {t('admin.createAccessLinks.form.shouldForceUsernames.label')}
                  </FormLabel>
                  <Tooltip label={t('admin.createAccessLinks.form.shouldForceUsernames.tooltip')}>
                    <IconButton
                      size="xs"
                      aria-label="Info"
                      icon={<QuestionIcon />}
                      variant="ghost"
                    />
                  </Tooltip>
                </Flex>
                <Controller
                  name="shouldForceUsernames"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      isChecked={field.value}
                      isDisabled={!linkFirstAccount} // disable toggle if linkFirstAccount = false
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  )}
                />
              </FormControl>

              {/* Forced usernames textarea */}
              {linkFirstAccount && shouldForceUsernames && (
                <FormControl isInvalid={!!errors.forcedUsernames}>
                  <Flex align="center" gap={2}>
                    <FormLabel m={0}>
                      {t('admin.createAccessLinks.form.forcedUsernames.label')}
                    </FormLabel>
                    <Tooltip label={t('admin.createAccessLinks.form.forcedUsernames.tooltip')}>
                      <IconButton
                        size="xs"
                        aria-label="Info"
                        icon={<QuestionIcon />}
                        variant="ghost"
                      />
                    </Tooltip>
                  </Flex>
                  <Controller
                    name="forcedUsernames"
                    control={control}
                    render={({ field }) => (
                      <Textarea
                        placeholder={
                          t('admin.createAccessLinks.form.forcedUsernames.placeholder') ?? undefined
                        }
                        rows={6}
                        value={(field.value ?? []).join('\n')}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value
                              .split('\n')
                              .map((s) => s.trim())
                              .filter(Boolean)
                          )
                        }
                      />
                    )}
                  />
                  {errors.forcedUsernames && (
                    <FormErrorMessage>{errors.forcedUsernames.message}</FormErrorMessage>
                  )}
                </FormControl>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter>
            <HStack spacing={3}>
              <Button variant="secondary" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                type="submit"
                isDisabled={isCreating}
                isLoading={isCreating}
              >
                {t('admin.createAccessLinks.form.submit')}
              </Button>
            </HStack>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
